import React, { createContext, useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { useAlert } from '../../alert/containers/AlertProvider'
import {
  getProfessionsApi,
  getAgeRangesApi,
  getContributorNumberApi,
  getContributorNumberByGenderApi,
  getContributorActiveRateApi,
  getContributorNumberByProfessionApi,
  getContributorNumberByAgeApi,
  getContributorTopWilayasApi,
  getComplaintNumberApi,
  getComplaintNumberByNature,
  getComplaintNumberByTrashType,
  getComplaintNumberByStatus,
  getComplaintTopWilayasApi,
  getComplaintsPerDairaApi,
  getComplaintsPerCommuneApi,
  getTrashTypesApi,
  getNaturesApi,
  getContributorCSVApi,
  getComplaintCSVApi,
} from '../api'
import { getWilayaLabel, getWilayaArabicLabel, getChartWilayaLabel, getChartWilayaArabicLabel } from '../../../utils/wilayasAndCommunes'
import { getDictionnaryLabelById, getDictionnaryArabicLabelById } from '../../../utils/dictionnary'
import 'moment/locale/fr'
import { useTranslation } from 'react-i18next'
import { getApiDairaName, getApiCommuneName } from '../../Intervenant/api'
import { useAuth } from '../../security/containers/AuthProvider'

moment.locale('fr')
export const DashboardContext = createContext()

export const initialContributorFilter = {
  genderIds: [],
  professionIds: [],
  wilayas: [],
  ageRangesIds: [],
  activeIds: [],
  communes: [],
  // mem
  dairaId: [],
}
export const initialComplaintFilter = {
  natureIds: [],
  trashTypeIds: [],
  wilayas: [],
  statuses: [],
  communes: [],
  // mem
  dairaId: [],
}

const initialDate = {
  // 29 days + today in order to have 30 days
  startDate: moment().subtract(29, 'd'),
  endDate: moment(),
}

const formatChartData = (chartData) =>
  chartData.reduce(
    (acc, curr) => {
      return {
        labels: [...acc.labels, curr.label],
        data: [...acc.data, curr.count],
        ids: [...acc.ids, curr.id],
      }
    },
    { labels: [], data: [], ids: [] },
  )

export const DashboardProvider = ({ children }) => {
  const { showAlert } = useAlert()
  const [isLoading, setIsLoading] = useState(false)
  const { t, i18n } = useTranslation()
  const { me } = useAuth()

  const [langDir, setlangDir] = useState(document.getElementsByTagName('html')[0].getAttribute('dir'))

  const statusesDictionnary = [
    { id: 1, label: t('complaintTab.nouvelle') },
    { id: 2, label: t('complaintTab.resolue') },
    { id: 3, label: t('complaintTab.annule') },
    { id: 4, label: t('complaintTab.assignee')},
    { id: 5, label: t('complaintTab.en_traitement')},
    { id: 6, label: t('complaintTab.returned')},
    { id: 7, label: t('complaintTab.relaunched')},
    { id: 8, label: t('complaintTab.treated')},
    { id: 9, label: t('complaintTab.confirmed')},
    { id: 10, label: t('complaintTab.unresolved') }
  ]

  const gendersDictionnary = [
    { id: 1, label: t('genders.homme')},
    { id: 2, label: t('genders.femme')},
    { id: -1, label: t('genders.nonPrecised')},
  ]

  const trashTypesDictionnary = [
    {id: 1, label: t('trashTypes.dechet_inertes')},
    {id: 2, label: t('trashTypes.dechet_vert')},
    {id: 3, label: t('trashTypes.dechet_menagers_assimile')},
    {id: 4, label: t('trashTypes.dechet_encombrant')},
    {id: 5, label: t('trashTypes.dechet_mixte')},
  ]

  const naturesDictionnary = [
    {id: 1, label: t('natures.depotoire_sauvage')},
    {id: 2, label: t('natures.collect_default')}
  ]

  // contributor dictionnary
  const [professions, setProfessions] = useState([])
  const [ageRanges, setAgeRanges] = useState([])

  const [contributorFilter, setContributorFilter] = useState(initialContributorFilter)

  const [contributorStat, setContributorStat] = useState({
    numberOfContributors: 0,
    genderStat: { labels: [], data: [] },
    activeContributorRate: { labels: [], data: [] },
    professionStat: { labels: [], data: [] },
    ageStat: { labels: [], data: [] },
    wilayaStat: { labels: [], data: []},
  })

  const [date, setDate] = useState(initialDate)
  // complaint dictionnary
  const [trashTypes, setTrashTypes] = useState([])
  const [natures, setNatures] = useState([])

  const [complaintFilter, setComplaintFilter] = useState(initialComplaintFilter)

  const [complaintStat, setComplaintStat] = useState({
    numberOfComplaints: 0,
    // numberOfResolvedComplaints: 0,
    // numberOfComplaintsInTreatment: 0,
    complaintNumberPerDay: 0,
    trashTypeStat: { labels: [], data: [] },
    natureStat: { labels: [], data: [] },
    statusStat: { labels: [], data: [] },
    wilayaStat: { labels: [], data: [], ids: [] },
    dairaStat: { labels: [], data: [], ids: [] },
  })

  const [intervenorDairaStat, setIntervenorDairaStat] = useState({ labels: [], data: [], ids: [] })
  const [communeStat, setCommuneStat] = useState({ labels: [], data: [], ids: [] })

  const [professionsPopOver, setprofessionsPopOver] = useState([])

  // get contributor dictionnary
  const getProfessions = async () => {
    try {
      setIsLoading(true)
      const { data } = await getProfessionsApi()
      setProfessions(data)
    } catch {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } finally {
      setIsLoading(false)
    }
  }
  const getAgeRanges = async () => {
    try {
      setIsLoading(true)
      const { data } = await getAgeRangesApi()
      setAgeRanges(data)
    } catch {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } finally {
      setIsLoading(false)
    }
  }

  // get contributor stat
  const getContributorStat = async () => {
    try {
      setIsLoading(true)

      // mem
      const {
        professionIds,
        genderIds,
        wilayas,
        communes,
        ageRangesIds,
        activeIds,
        // mem
        dairaId,
      } = contributorFilter
      
      let active = null
      if (activeIds.length === 1 && activeIds[0] === 1) {
        active = true
      } else if (activeIds.length === 1) {
        active = false
      }

      // mem
      const requestFilter = {
        professions: professionIds,
        genders: genderIds,
        communeIds: communes.map(({ id }) => id),
        // mem
        dairaIds: dairaId.map(({ id }) => id),
        //
        ageRanges: ageRangesIds,
        active,
        wilayaIds: wilayas.map(({ id }) => id),
        ...(date.startDate !== null && {
          dateStart: moment(
            `${date.startDate.format('YYYY-MM-DD')} 00:00:00`,
          ).format(),
        }),
        ...(date.endDate !== null && {
          dateEnd: moment(
            `${date.endDate.format('YYYY-MM-DD')} 23:59:59`,
          ).format(),
        }),
      }

      const requestsData = await Promise.all([
        getContributorNumberApi({
          filter: requestFilter,
        }),
        getContributorNumberByGenderApi({
          filter: requestFilter,
        }),
        getContributorActiveRateApi({
          filter: requestFilter,
        }),
        getContributorNumberByProfessionApi({
          filter: requestFilter,
        }),
        getContributorNumberByAgeApi({
          filter: requestFilter,
        }),
        getContributorTopWilayasApi({
          filter: requestFilter,
        }),
      ])

      const [
        numberOfContributors,
        genderStat,
        activeContributorRate,
        professionStat,
        ageStat,
        wilayaStat,
      ] = requestsData.map(({ data }) => data)

      setContributorStat({
        numberOfContributors,
        genderStat: formatChartData(
          genderStat.map(({ count, label}) => ({
            label: label === 'Homme' ? t('genders.homme') 
                  : label === 'Femme' ? t('genders.femme')
                  : label === 'N/A' ? t('professions.nonPrecised')
                  : undefined,
            count
          }))
        ),
        activeContributorRate: formatChartData(
          activeContributorRate.map(({ count, label }) => ({
            label: label === "Actifs" ? t('activeContributor.actif')
                 : label === "Innactifs" ? t('activeContributor.nonActif')
                 : undefined,
            count
          }))),
        professionStat: formatChartData(
          professionStat.map(({ count, label }) => ({
            label: label === "N/A" ? t('professions.nonPrecised')
                 : label === "Sans emploi" ? t('professions.sansEmploi')
                 : label === "Etudiant" ? t('professions.etudiant')
                 : label === "Employé" ? t('professions.employed')
                 : label === "Commerçant/profession libérale" ? t('professions.commercant_liberalProfession')
                 : label === "Chef d’entreprise" ? t('professions.chefEntreprise') 
                 : label === "Autre" ? t('professions.other')
                 : undefined,
            count
          }))
        ),
        ageStat: formatChartData(
          ageStat.map(({ count, label }) => ({
            label: label === "N/A" ? t('professions.nonPrecised')
                 : label,
            count
          }))
        ),
        wilayaStat: formatChartData(
          document.getElementsByTagName('html')[0].getAttribute('dir') === "ltr" ?
          wilayaStat.map(({ label, count }) => ({
            label: getChartWilayaLabel(label) === 'N/A'
            ? 'N/A'
            : `${getChartWilayaLabel(label)}`,
            count
          }))
          :
          wilayaStat.map(({ label, count }) => ({
            label: getChartWilayaArabicLabel(label) === 'N/A'
            ? 'N/A'
            : `${getChartWilayaArabicLabel(label)}`,
            count
          }))
        ),
      })
    } catch (e) {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } finally {
      setIsLoading(false)
    }
  }

  // get complaint dictionnary
  const getTrashTypes = async () => {
    try {
      setIsLoading(true)
      const { data } = await getTrashTypesApi()
      setTrashTypes(data)
    } catch {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } finally {
      setIsLoading(false)
    }
  }

  const getNatures = async () => {
    try {
      setIsLoading(true)
      const { data } = await getNaturesApi()
      setNatures(data)
    } catch {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } finally {
      setIsLoading(false)
    }
  }
  
  const getComplaintStat = async () => {
    try {
      setIsLoading(true)
      
      // mem
      const { natureIds, trashTypeIds, wilayas, statuses, communes, dairaId } = complaintFilter
      
      let numberOfDays = date.endDate.diff(date.startDate, 'days')
      numberOfDays = numberOfDays > 0 ? numberOfDays : 10
      
      // mem
      const requestFilter = {
        trashTypeIds,
        natureIds,
        communeIds: communes.map(({ id }) => id),
        // mem
        dairaIds: dairaId.map(({ id }) => id),
        //
        statuses,
        wilayaIds: wilayas.map(({ id }) => id),
        ...(date.startDate !== null && {
          startDate: moment(
            `${date.startDate.format('YYYY-MM-DD')} 00:00:00`,
          ).format(),
        }),
        ...(date.endDate !== null && {
          endDate: moment(
            `${date.endDate.format('YYYY-MM-DD')} 23:59:59`,
          ).format(),
        }),
      }

      const requestsData = await Promise.all([
        getComplaintNumberApi({
          filter: requestFilter,
        }),
        getComplaintNumberByNature({
          filter: requestFilter,
        }),
        getComplaintNumberByTrashType({
          filter: requestFilter,
        }),
        getComplaintNumberByStatus({
          filter: requestFilter,
        }),
        getComplaintTopWilayasApi({
          filter: requestFilter,
        }),
      ])

      const [
        numberOfComplaints,
        natureStat,
        trashTypeStat,
        statusStat,
        wilayaStat,
      ] = requestsData.map(({ data }) => data)

      if(document.getElementsByTagName('html')[0].getAttribute('dir') === "ltr") {
        setlangDir("ltr")
        setComplaintStat({
          numberOfComplaints,
          complaintNumberPerDay: numberOfComplaints / numberOfDays,
          trashTypeStat: formatChartData(
              trashTypeStat.map(({ key, count }) => ({
              label: getDictionnaryLabelById({
                id: key,
                dictionnary: trashTypes,
              }),
              count,
            }))
          ),
          resolvedComplaintsNumber:
            statusStat.find(({key, count}) => key === 2 ? count : 0 ),
          onTreatmentComplaintsNumber: 
            statusStat.find(({ key, count }) => key === 5 ? count : 0 ),
          natureStat: formatChartData(
            natureStat.map(({ key, count }) => ({
              label: getDictionnaryLabelById({ id: key, dictionnary: natures }),
              count,
            }))
          ),

          statusStat: formatChartData(
            statusStat.map(({ key, count }) => ({
              label: getDictionnaryLabelById({
                id: key,
                dictionnary: statusesDictionnary,
              }),
              count,
            })),
          ),

          wilayaStat: formatChartData(
            wilayaStat.map(({ key, count }) => ({
              label: getWilayaLabel(key) === 'N/A'
              ? 'N/A'
              : `${getWilayaLabel(key)}`,
              count,
              id: key
            }))
          ) 
        })
      } 
      else {
        setlangDir("rtl")
        setComplaintStat({
          numberOfComplaints,
          complaintNumberPerDay: numberOfComplaints / numberOfDays,
          trashTypeStat: formatChartData(
              trashTypeStat.map(({ key, count }) => ({
              label: getDictionnaryArabicLabelById({
                id: key,
                dictionnary: trashTypes,
              }),
              count,
            }))
          ),

          resolvedComplaintsNumber:
            statusStat.find(({key, count}) => key === 2 ? count : 0 ),

          onTreatmentComplaintsNumber: 
            statusStat.find(({ key, count }) => key === 5 ? count : 0 ),
          
            natureStat: formatChartData(
            natureStat.map(({ key, count }) => ({
              label: getDictionnaryArabicLabelById({ id: key, dictionnary: natures }),
              count,
            }))
          ),

          statusStat: formatChartData(
            statusStat.map(({ key, count }) => ({
              label: getDictionnaryLabelById({
                id: key,
                dictionnary: statusesDictionnary,
              }),
              count,
            })),
          ),
          
          wilayaStat: formatChartData(
            wilayaStat.map(({ key, count }) => ({
              label: getWilayaArabicLabel(key) === 'N/A'
              ? 'N/A'
              : `${getWilayaArabicLabel(key)}`,
              count,
              id: key
            }))
          ) 
        })
      }
    } 
    catch (e) {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } 
    finally {
      setIsLoading(false)
    }
  }

  const getContributorCSV = () => {
    const {
      professionIds,
      genderIds,
      wilayas,
      ageRangesIds,
      activeIds,
      // mem
      communes,
      dairaId
    } = contributorFilter

    let active = null
    if (activeIds.length === 1 && activeIds[0] === 1) {
      active = true
    } else if (activeIds.length === 1) {
      active = false
    }

    const requestFilter = {
      professions: professionIds,
      genders: genderIds,
      // mem
      communeIds: communes.map(({ id }) => id),
      dairaIds: dairaId.map(({ id }) => id),
      //
      ageRanges: ageRangesIds,
      active,
      wilayaIds: wilayas.map(({ id }) => id),
      ...(date.startDate !== null && {
        dateStart: moment(
          `${date.startDate.format('YYYY-MM-DD')} 00:00:00`,
        ).format(),
      }),
      ...(date.endDate !== null && {
        dateEnd: moment(
          `${date.endDate.format('YYYY-MM-DD')} 23:59:59`,
        ).format(),
      }),
    }

    getContributorCSVApi({ filter: requestFilter })
      .then((res) => new Blob([res.data], { type: 'text/csv' }))
      .then((blob) => {
        saveAs(blob, `statistique_ndif_utilisateur_${Date.now()}.csv`)
      })
  }

  const getComplaintCSV = () => {
    const { natureIds, 
      trashTypeIds, 
      wilayas, 
      statuses,
      // mem 
      communes,
      dairaId,
      //
    } = complaintFilter

    const requestFilter = {
      trashTypeIds,
      natureIds,
      // mem
      communeIds: communes.map(({ id }) => id),
      dairaIds: dairaId.map(({ id }) => id),
      //
      statuses,
      wilayaIds: wilayas.map(({ id }) => id),
      ...(date.startDate !== null && {
        startDate: moment(
          `${date.startDate.format('YYYY-MM-DD')} 00:00:00`,
        ).format(),
      }),
      ...(date.endDate !== null && {
        endDate: moment(
          `${date.endDate.format('YYYY-MM-DD')} 23:59:59`,
        ).format(),
      }),
    }

    getComplaintCSVApi({ filter: requestFilter })
      .then((res) => new Blob([res.data], { type: 'text/csv' }))
      .then((blob) => {
        saveAs(blob, `statistique_ndif_réclamation_${Date.now()}.csv`)
      })
  }

  const init = () => {
    setComplaintFilter({ ...initialComplaintFilter })
    setContributorFilter({ ...initialContributorFilter })
    setDate({ ...initialDate })
  }

  //
  const mapProfessionsPopOver = () => {
    let finalResult = []

    if (i18n.language === "fr")
      finalResult = professions.map(({id, label}) => ({id:id, label:label}) )
    else
      finalResult = professions.map(({id, label_ar}) => ({id:id, label:label_ar}) )
    
    setprofessionsPopOver(finalResult)
  }

  const getDairaStat = async (wilayaId) => {
    try {
      const { natureIds, trashTypeIds, statuses, communes, dairaId, wilayas } = complaintFilter

      let requestFilter = {
        trashTypeIds,
        natureIds,
        communeIds: communes.map(({ id }) => id),
        dairaIds: dairaId.map(({ id }) => id),
        statuses,
        wilayaIds: wilayaId === undefined ? wilayas.map(({ id }) => id) : [wilayaId],
        ...(date.startDate !== null && {
          startDate: moment(
            `${date.startDate.format('YYYY-MM-DD')} 00:00:00`,
          ).format(),
        }),
        ...(date.endDate !== null && {
          endDate: moment(
            `${date.endDate.format('YYYY-MM-DD')} 23:59:59`,
          ).format(),
        }),
      }

      setIsLoading(true)
      const { data } = await getComplaintsPerDairaApi({filter: requestFilter})

      let dairaIds = data.map((obj) => {
        return obj.key
      })
  
      let dairaNames = await getApiDairaName(dairaIds)

      let dairaStat = formatChartData(
        i18n.language === "fr" ?
        data.map(({ key, count }) => ({
          label: dairaNames.data.find(daira => daira.id === key).name === 'N/A'
          ? 'N/A'
          : `${dairaNames.data.find(daira => daira.id === key).name}`,
          count,
          id: key
        }))
        :
        data.map(({ key, count }) => ({
          label: dairaNames.data.find(daira => daira.id === key).name_ar === 'N/A'
          ? 'N/A'
          : `${dairaNames.data.find(daira => daira.id === key).name_ar}`,
          count,
          id: key
        }))
      )
      if (me?.roles[0] !== "intervenor")  setComplaintStat({...complaintStat, dairaStat: dairaStat})
      else  setIntervenorDairaStat(dairaStat)
    }
    catch (e) {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } 
    finally {
      setIsLoading(false)
    }
  }

  const getCommuneStat = async () => {
    try {
      const { natureIds, trashTypeIds, statuses, communes, dairaId, wilayas } = complaintFilter

      let requestFilter = {
        trashTypeIds,
        natureIds,
        communeIds: communes.map(({ id }) => id),
        dairaIds: dairaId.map(({ id }) => id),
        statuses,
        wilayaIds: wilayas.map(({ id }) => id),
        ...(date.startDate !== null && {
          startDate: moment(
            `${date.startDate.format('YYYY-MM-DD')} 00:00:00`,
          ).format(),
        }),
        ...(date.endDate !== null && {
          endDate: moment(
            `${date.endDate.format('YYYY-MM-DD')} 23:59:59`,
          ).format(),
        }),
      }

      setIsLoading(true)
      const { data } = await getComplaintsPerCommuneApi({filter: requestFilter})

      let communeIds = data.map((obj) => {
        return obj.key
      })
  
      let communeNames = await getApiCommuneName(communeIds)

      let communeStat = formatChartData(
        i18n.language === "fr" ?
        data.map(({ key, count }) => ({
          label: communeNames.data.find(commune => commune.id === key).name === 'N/A'
          ? 'N/A'
          : `${communeNames.data.find(commune => commune.id === key).name}`,
          count,
          id: key
        }))
        :
        data.map(({ key, count }) => ({
          label: communeNames.data.find(commune => commune.id === key).name_ar === 'N/A'
          ? 'N/A'
          : `${communeNames.data.find(commune => commune.id === key).name_ar}`,
          count,
          id: key
        }))
      )
      setCommuneStat(communeStat)
    }
    catch (e) {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } 
    finally {
      setIsLoading(false)
    }
  }

  const providerValue = {
    init,
    professions,
    ageRanges,
    genders: gendersDictionnary,
    actives: [
      { id: 1, label: t('activeContributor.actif') },
      { id: 2, label: t('activeContributor.nonActif') },
    ],
    contributorStat,
    contributorFilter,
    isLoading,
    setContributorFilter,
    getContributorStat,
    //
    statuses: statusesDictionnary,
    trashTypes: trashTypesDictionnary,
    natures: naturesDictionnary,
    complaintStat,
    complaintFilter,
    setComplaintFilter,
    getComplaintStat,
    getContributorCSV,
    getComplaintCSV,
    date,
    setDate,
    setlangDir,
    professionsPopOver,
    getDairaStat,
    intervenorDairaStat,
    communeStat,
  }

  useEffect(() => {
    if ((professions.length > 0, ageRanges.length > 0)) {

      mapProfessionsPopOver()
      getContributorStat()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contributorFilter, professions, ageRanges, date, langDir])

  useEffect(() => {
    if ((natures.length > 0, trashTypes.length > 0)) {
      getComplaintStat()
      if (me?.roles[0] === "intervenor") {
        if ((me?.entityType === 9 || me?.entityType === 10)) {
          getDairaStat()
        }
        else if(me?.entityType === 11) {
          getCommuneStat()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [natures, trashTypes, complaintFilter, date, langDir])

  useEffect(() => {
    getProfessions()
    getAgeRanges()
    getTrashTypes()
    getNatures()

    //
    mapProfessionsPopOver()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <DashboardContext.Provider value={providerValue}>
      {children}
    </DashboardContext.Provider>
  )
}
DashboardProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useDashboard = () => {
  const context = useContext(DashboardContext)
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider')
  }
  return context
}
